import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import moment from 'moment';
import Select from 'react-select';
import { Nav, NavItem, NavLink, TabContent, TabPane, Button, Badge, Table, Modal, ModalBody, ModalHeader } from 'reactstrap';
import {
  getWorkspaceMembers,
  getWorkspaceContacts,
  getWorkspaceDeals,
  getWorkspaceAgents,
  getWorkspaceActivity,
  getOwnerWorkspaces,
  getAccountAgents,
  getWorkspacePaymentHistory
} from '../../../store/Admin';
import { formatNumber } from '../../../utils/formatNumber';
import { getWorkspaceEffectiveState, LOCK_REASON_LABELS } from './workspaceState';
import './Workspaces.css';
import notFoundImg from '../../../static/img/not-found.png';

const WorkspaceSidebar = ({ isOpen, onClose, workspace }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState({});
  const [activityMemberId, setActivityMemberId] = useState('');

  const {
    workspaceMembers = [],
    workspaceContacts = [],
    workspaceDeals = [],
    workspaceAgents = [],
    workspaceActivity = [],
    ownerWorkspaces = [],
    ownerWorkspacesData,
    accountAgentsData,
    workspacePaymentHistory,
    loadingMembers,
    loadingContacts,
    loadingDeals,
    loadingAgents,
    loadingActivity,
    loadingOwnerWorkspaces,
    loadingAccountAgents,
    loadingPaymentHistory
  } = useSelector((state) => {
    console.log('Full Admin state:', state.Admin);
    console.log('workspaceActivity from state:', state.Admin.workspaceActivity);
    console.log('loadingActivity from state:', state.Admin.loadingActivity);
    return state.Admin;
  });

  useEffect(() => {
    if (workspace && workspace._id && isOpen) {
      // Reason: the member filter belongs to the previous workspace — clear it before loading the new one
      setActivityMemberId('');
      loadTabData(activeTab, '');
      // Reason: the header plan box shows total spent, which only the payment-history endpoint provides
      dispatch(getWorkspacePaymentHistory({ id: workspace._id }));
    }
  }, [workspace, isOpen]);

  useEffect(() => {
    if (workspace && workspace._id && isOpen) {
      loadTabData(activeTab);
    }
  }, [activeTab]);

  const loadTabData = (tab, memberIdOverride) => {
    if (!workspace || !workspace._id) return;

    switch (tab) {
      case 'members':
        dispatch(getWorkspaceMembers({ id: workspace._id }));
        break;
      case 'contacts':
        dispatch(getWorkspaceContacts({ id: workspace._id }));
        break;
      case 'deals':
        dispatch(getWorkspaceDeals({ id: workspace._id }));
        break;
      case 'agents':
        dispatch(getWorkspaceAgents({ id: workspace._id }));
        break;
      case 'activity': {
        const activityUserId = memberIdOverride !== undefined ? memberIdOverride : activityMemberId;
        dispatch(getWorkspaceActivity({ id: workspace._id, ...(activityUserId && { userId: activityUserId }) }));
        // Reason: the member filter dropdown needs the member list even if the Members tab was never opened
        dispatch(getWorkspaceMembers({ id: workspace._id }));
        break;
      }
      case 'workspaces':
        dispatch(getOwnerWorkspaces({ id: workspace._id }));
        break;
      case 'paymentHistory':
        dispatch(getWorkspacePaymentHistory({ id: workspace._id }));
        break;
      default:
        break;
    }
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const renderMembersTab = () => (
    <div className='content-dashboard'>
      <div className="table-container filters-table">
        <div className='table-area'>
          {loadingMembers ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : workspaceMembers.length > 0 ? (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {workspaceMembers.map((member, index) => (
                  <tr key={index} className={member.isAdmin ? '' : ''}>
                    <td>
                      <div className="d-flex align-items-center">
                        {member.isAdmin && (
                          <i className="fa fa-crown mr-2 text-warning" title="Administrator"></i>
                        )}
                        <strong>
                          {member.userId ?
                            `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim() || 'N/A'
                            : 'N/A'}
                        </strong>
                      </div>
                    </td>
                    <td>{member.email || (member.userId?.email) || 'N/A'}</td>
                    <td>
                      <Badge color={
                        member.role === 'admin' ? 'warning' :
                          member.role === 'owner' ? 'danger' :
                            'info'
                      }>
                        {member.role || 'member'}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={member.status === 'active' ? 'success' : 'secondary'}>
                        {member.status || 'pending'}
                      </Badge>
                    </td>
                    <td>{moment(member.createdAt).format('MMM DD, YYYY')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className='notfound-area'>
              <div className='img-box'>
                <img alt="..." src={notFoundImg} />
              </div>
              <p>No members found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderContactsTab = () => (
    <div className='content-dashboard'>
      <div className="table-container filters-table">
        <div className='table-area'>
          {loadingContacts ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : workspaceContacts.length > 0 ? (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {workspaceContacts.map((contact, index) => (
                  <tr key={index}>
                    <td>{`${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'N/A'}</td>
                    <td>{contact.email || 'N/A'}</td>
                    <td>{contact.company || 'N/A'}</td>
                    <td>
                      <Badge color={
                        contact.status === 'active' ? 'success' :
                          contact.status === 'lead' ? 'info' :
                            contact.status === 'customer' ? 'primary' : 'secondary'
                      }>
                        {contact.status || 'active'}
                      </Badge>
                    </td>
                    <td>{moment(contact.createdAt).format('MMM DD, YYYY')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className='notfound-area'>
              <div className='img-box'>
                <img alt="..." src={notFoundImg} />
              </div>
              <p>No contacts found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDealsTab = () => (
    <div className='content-dashboard'>
      <div className="table-container filters-table">
        <div className='table-area'>
          {loadingDeals ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : workspaceDeals.length > 0 ? (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Amount</th>
                  <th>Stage</th>
                  <th>Pipeline</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {workspaceDeals.map((deal, index) => (
                  <tr key={index}>
                    <td>{deal.title || 'N/A'}</td>
                    <td>${(deal.amount || 0).toLocaleString()}</td>
                    <td>
                      <Badge color="info">{deal.stage || 'N/A'}</Badge>
                    </td>
                    <td>{deal.pipelineId?.name || 'N/A'}</td>
                    <td>{moment(deal.createdAt).format('MMM DD, YYYY')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className='notfound-area'>
              <div className='img-box'>
                <img alt="..." src={notFoundImg} />
              </div>
              <p>No deals found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAgentsTab = () => {
    return (
      <div className='content-dashboard'>
        <div className="table-container filters-table">
          <div className='table-area'>
            {loadingAgents ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : workspaceAgents.length > 0 ? (
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Agent Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Call Stats</th>
                    <th>Tags</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {workspaceAgents.map((agent, index) => (
                    <tr key={index}>
                      <td>
                        <i className="fa fa-robot mr-2 text-info"></i>
                        <strong>{agent.name || 'Unnamed Agent'}</strong>
                      </td>
                      <td>
                        <Badge color="primary" className="text-capitalize">
                          {agent.type || 'Single Prompt'}
                        </Badge>
                      </td>
                      <td>
                        <Badge color={agent.status === 'active' ? 'success' : 'secondary'}>
                          {agent.status || 'inactive'}
                        </Badge>
                      </td>
                      <td>
                        {agent.callStats ? (
                          <div>
                            <small>Total: {agent.callStats.totalCalls || 0}</small><br />
                            <small>Success: {agent.callStats.successRate || 0}%</small>
                          </div>
                        ) : (
                          <small className="text-muted">No calls yet</small>
                        )}
                      </td>
                      <td>
                        {agent.tags && agent.tags.length > 0 ? (
                          agent.tags.map((tag, idx) => (
                            <Badge key={idx} color="light" className="mr-1">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{moment(agent.createdAt).format('MMM DD, YYYY')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className='notfound-area'>
                <div className='img-box'>
                  <img alt="..." src={notFoundImg} />
                </div>
                <p>No agents found in this workspace</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderWorkspacesTab = () => {
    // Extract owner email and total workspaces from the response
    const ownerEmail = ownerWorkspacesData?.ownerEmail;
    const totalWorkspaces = ownerWorkspacesData?.totalWorkspaces;

    return (
      <div className='content-dashboard'>
        <div className="table-container filters-table workspaces-table-user">
          <div className='table-area'>
            {loadingOwnerWorkspaces ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {ownerEmail && (
                  <div className='function-box table-owner-area'>
                    <div className="user-info">
                      <div className="user-title-section">
                        <h3 className="user-name">
                          <i className="icon">
                            <svg fill="currentColor" height="800px" width="800px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" enable-background="new 0 0 64 64">
                              <path id="Mail" stroke="currentColor" d="M58.0034485,8H5.9965506c-3.3136795,0-5.9999995,2.6862001-5.9999995,6v36c0,3.3137016,2.6863203,6,5.9999995,6
                                h52.006897c3.3137016,0,6-2.6862984,6-6V14C64.0034485,10.6862001,61.3171501,8,58.0034485,8z M62.0034485,49.1108017
                                L43.084549,30.1919994l18.9188995-12.0555992V49.1108017z M5.9965506,10h52.006897c2.2056007,0,4,1.7943001,4,4v1.7664003
                                L34.4677505,33.3134003c-1.4902,0.9492989-3.3935013,0.9199982-4.8495998-0.0703011L1.9965508,14.4694996V14
                                C1.9965508,11.7943001,3.7910507,10,5.9965506,10z M1.9965508,16.8852005L21.182251,29.9251003L1.9965508,49.1108017V16.8852005z
                                M58.0034485,54H5.9965506c-1.6473999,0-3.0638998-1.0021019-3.6760998-2.4278984l20.5199013-20.5200024l5.6547985,3.843401
                                c1.0859013,0.7383003,2.3418007,1.1083984,3.5995998,1.1083984c1.1953011,0,2.3925018-0.3339996,3.4463005-1.0048981
                                l5.8423996-3.7230015l20.2961006,20.2961025C61.0673485,52.9978981,59.6508713,54,58.0034485,54z"/>
                            </svg>
                          </i>
                          <b>Account Email: </b> {ownerEmail}
                        </h3>
                        <span>
                          <Badge color="primary" className="px-3 py-2">
                            Total Workspaces: {totalWorkspaces || ownerWorkspaces.length}
                          </Badge>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {ownerWorkspaces.length > 0 ? (
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Workspace Name</th>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Plan</th>
                        <th>Members</th>
                        <th>Agents</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerWorkspaces.map((ws, index) => (
                        <tr key={index} className={ws.isCurrentWorkspace ? 'table-active' : ''}>
                          <td>
                            <strong>{ws.name || 'N/A'}</strong>
                            {ws.isCurrentWorkspace && (
                              <Badge color="info" className="ml-2">Current</Badge>
                            )}
                          </td>
                          <td><small className="text-muted">{ws.id || ws._id}</small></td>
                          <td>
                            <Badge color={
                              ws.status === 'active' ? 'success' :
                                ws.status === 'suspended' ? 'warning' :
                                  ws.status === 'trial' ? 'info' : 'secondary'
                            }>
                              {ws.status || 'active'}
                            </Badge>
                          </td>
                          <td>
                            <Badge color={ws.currentSubscription?.planName === 'Free' ? 'secondary' : 'primary'}>
                              {ws.currentSubscription?.planName || 'Free'}
                            </Badge>
                          </td>
                          <td>
                            <Badge color="light" className="text-dark">
                              {ws.memberCount || 0}
                            </Badge>
                          </td>
                          <td>
                            <Badge color="light" className="text-dark">
                              {ws.agentCount || 0}
                            </Badge>
                          </td>
                          <td>{moment(ws.createdAt).format('MMM DD, YYYY')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className='notfound-area'>
                    <div className='img-box'>
                      <img alt="..." src={notFoundImg} />
                    </div>
                    <p>No workspaces found for this account</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentHistoryTab = () => {
    const paymentData = workspacePaymentHistory;

    return (
      <div className='content-dashboard'>

        <div className="table-container filters-table workspaces-table-user">
          <div className='table-area'>
            {loadingPaymentHistory ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {paymentData?.payments?.length > 0 ? (
                  <>
                    <h5 className="mb-3 ml-3">Payment History</h5>
                    <Table responsive hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Invoice #</th>
                          <th>Amount</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Description</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentData.payments.map((payment, index) => (
                          <tr key={index}>
                            <td>{payment.invoiceNumber || '-'}</td>
                            <td>
                              <strong>${formatNumber(payment.amount || 0)} {payment.currency}</strong>
                            </td>
                            <td>
                              <Badge color="info" className="text-capitalize">
                                {payment.type}
                              </Badge>
                            </td>
                            <td>
                              <Badge color={
                                payment.status === 'succeeded' ? 'success' :
                                  payment.status === 'pending' ? 'warning' :
                                    payment.status === 'failed' ? 'danger' : 'secondary'
                              }>
                                {payment.status}
                              </Badge>
                            </td>
                            <td>{formatDescriptionNumbers(payment.description) || '-'}</td>
                            <td>{moment(payment.paidAt || payment.createdAt).format('MMM DD, YYYY')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                ) : (
                  <div className='notfound-area'>
                    <div className='img-box'>
                      <img alt="..." src={notFoundImg} />
                    </div>
                    <p>No payment history found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper functions for activity display - matching Dashboard and User activity style
  const getActivityIcon = (type) => {
    const icons = {
      'user_created': 'fa-user-plus',
      'user_updated': 'fa-user-edit',
      'user_deleted': 'fa-user-times',
      'workspace_created': 'fa-building',
      'workspace_updated': 'fa-edit',
      'workspace_suspended': 'fa-pause-circle',
      'workspace_deleted': 'fa-trash-alt',
      'agent_created': 'fa-robot',
      'agent_started': 'fa-play-circle',
      'agent_stopped': 'fa-stop-circle',
      'agent_deleted': 'fa-trash-alt',
      'system_backup': 'fa-database',
      'system_update': 'fa-sync-alt',
      'system_error': 'fa-exclamation-triangle',
      'login_success': 'fa-sign-in-alt',
      'login_failed': 'fa-times-circle',
      'subscription_created': 'fa-credit-card',
      'subscription_upgraded': 'fa-arrow-up',
      'subscription_cancelled': 'fa-times',
      'payment_success': 'fa-dollar-sign',
      'payment_failed': 'fa-exclamation-circle',
      'security_alert': 'fa-shield-alt',
      'api_key_created': 'fa-key',
      'api_key_revoked': 'fa-ban',
      'authentication': 'fa-lock',
      'contact': 'fa-address-book',
      'deal': 'fa-handshake-o',
      'email': 'fa-envelope',
      'call': 'fa-phone'
    };
    return icons[type] || 'fa-info-circle';
  };

  const getActivityColor = (severity) => {
    const colors = { 'success': 'text-success', 'info': 'text-info', 'warning': 'text-warning', 'error': 'text-danger', 'critical': 'text-danger' };
    return colors[severity] || 'text-secondary';
  };

  const getRelativeTime = (timestamp) => {
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
  };

  const handleActivityMemberChange = (option) => {
    const memberId = option?.['value'] || '';
    setActivityMemberId(memberId);
    dispatch(getWorkspaceActivity({ id: workspace._id, ...(memberId && { userId: memberId }) }));
  };

  const renderActivityTab = () => {
    const memberOptions = [
      { value: '', label: 'All members', email: '' },
      ...workspaceMembers.filter(member => member.userId?._id).map(member => ({ value: member['userId']['_id'], label: `${member['userId']['firstName'] || ''} ${member['userId']['lastName'] || ''}`.trim() || member['userId']['email'] || member['email'], email: member['userId']['email'] || member['email'] || '' }))
    ];

    return (
      <div className='content-dashboard mt-3'>
        <div className="workspace-activity-filter">
          <h6>Recent Activity</h6>
          <Select
            className="workspace-activity-member-select"
            classNamePrefix="activity-member"
            value={memberOptions.find(option => option['value'] === activityMemberId) || memberOptions[0]}
            onChange={handleActivityMemberChange}
            options={memberOptions}
            formatOptionLabel={(option, { context }) => context === 'menu' ? (
              <div className="workspace-activity-member-option">
                <span className="workspace-activity-member-name">{option['label']}</span>
                {option['email'] && <span className="workspace-activity-member-email">{option['email']}</span>}
              </div>
            ) : option['label']}
          />
        </div>
        <div className="activity-list workspace-activity-list">
          {loadingActivity ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {(workspaceActivity && workspaceActivity.length > 0 ? workspaceActivity : []).map(activity => (
                <div key={activity['_id']} className="activity-item mb-3 pb-2 border-bottom">
                  <div className="d-flex align-items-start">
                    <div className="activity-icon">
                      <i className={`fa ${getActivityIcon(activity['type'])} ${getActivityColor(activity['severity'])}`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="activity-content">
                        <p className="mb-1 small">
                          <strong>{activity['actor']?.['name'] || activity['actor']?.['email'] || 'User'}</strong> {activity['action']}
                          {activity['workspace'] && (
                            <span>&nbsp;in <strong>{activity['workspace']['name']}</strong></span>
                          )}
                        </p>
                        <small className="text-muted">
                          <i className="fa fa-clock mr-1"></i>
                          {getRelativeTime(activity['timestamp'])}
                          {activity['target']?.['type'] && (
                            <span className="ml-2">
                              <Badge color="secondary" className="badge-sm">{activity['target']['type']}</Badge>
                            </span>
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {(!workspaceActivity || workspaceActivity.length === 0) && (
                <div className="text-center text-muted py-4">
                  <i className="fa fa-clock fa-2x mb-2 d-block"></i>
                  <p>{activityMemberId ? 'No recent activity found for this member' : 'No recent activity found for this workspace'}</p>
                  <small>Activities will appear here when actions are performed</small>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Reason: the list row stores the plan under currentSubscription, while the details endpoint returns plan/subscription — check all three so paid plans don't fall back to 'Free'
  const planName = workspace?.plan || workspace?.currentSubscription?.planName || workspace?.subscription?.planName || 'Free';
  const subscription = workspace?.currentSubscription || workspace?.subscription || null;
  const planInfo = parsePlanName(planName);
  // Reason: the list row carries the populated plan doc (planId.price), the details endpoint returns a flat price
  const planPrice = subscription?.planId?.price ?? subscription?.price ?? null;
  const effectiveState = getWorkspaceEffectiveState(workspace, subscription);

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} modalClassName='right right-without-bg' backdrop={false} className={`main-modal right right-side-modal style-three`}>
        <ModalHeader toggle={onClose}>
          <h3>{workspace?.name || 'Workspace Details'}
            <Badge color={effectiveState.color} className="status-badge">
              {effectiveState.label}
            </Badge>
          </h3>
        </ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>
            <div className='function-box workspace-area'>

              <div className="user-info">
                <div className="user-title-section">
                  <h3 className="user-name">
                    <i className="icon">
                      <svg fill="currentColor" height="800px" width="800px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" enable-background="new 0 0 64 64">
                        <path id="Mail" stroke="currentColor" d="M58.0034485,8H5.9965506c-3.3136795,0-5.9999995,2.6862001-5.9999995,6v36c0,3.3137016,2.6863203,6,5.9999995,6
                                h52.006897c3.3137016,0,6-2.6862984,6-6V14C64.0034485,10.6862001,61.3171501,8,58.0034485,8z M62.0034485,49.1108017
                                L43.084549,30.1919994l18.9188995-12.0555992V49.1108017z M5.9965506,10h52.006897c2.2056007,0,4,1.7943001,4,4v1.7664003
                                L34.4677505,33.3134003c-1.4902,0.9492989-3.3935013,0.9199982-4.8495998-0.0703011L1.9965508,14.4694996V14
                                C1.9965508,11.7943001,3.7910507,10,5.9965506,10z M1.9965508,16.8852005L21.182251,29.9251003L1.9965508,49.1108017V16.8852005z
                                M58.0034485,54H5.9965506c-1.6473999,0-3.0638998-1.0021019-3.6760998-2.4278984l20.5199013-20.5200024l5.6547985,3.843401
                                c1.0859013,0.7383003,2.3418007,1.1083984,3.5995998,1.1083984c1.1953011,0,2.3925018-0.3339996,3.4463005-1.0048981
                                l5.8423996-3.7230015l20.2961006,20.2961025C61.0673485,52.9978981,59.6508713,54,58.0034485,54z"/>
                      </svg>
                    </i>
                    Email
                  </h3>
                  <span title={workspace?.owner?.email || workspace?.createdBy?.email || 'N/A'}>
                    {workspace?.owner?.email || workspace?.createdBy?.email || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="user-info">
                <div className="user-title-section">
                  <h3 className="user-name">
                    <i className="icon">
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 21C5 17.134 8.13401 14 12 14C15.866 14 19 17.134 19 21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                    </i>
                    Owner
                  </h3>
                  <span title={workspace?.owner ?
                    `${workspace.owner.firstName || ''} ${workspace.owner.lastName || ''}`.trim() || 'N/A' :
                    workspace?.createdBy ?
                      `${workspace.createdBy.firstName || ''} ${workspace.createdBy.lastName || ''}`.trim() || 'N/A' :
                      'N/A'}>
                    {workspace?.owner ?
                      `${workspace.owner.firstName || ''} ${workspace.owner.lastName || ''}`.trim() || 'N/A' :
                      workspace?.createdBy ?
                        `${workspace.createdBy.firstName || ''} ${workspace.createdBy.lastName || ''}`.trim() || 'N/A' :
                        'N/A'
                    }
                  </span>
                </div>
              </div>
              <div className="user-info">
                <div className="user-title-section">
                  <h3 className="user-name">
                    <i class="fa fa-calendar mr-2"></i>
                    Created
                  </h3>
                  <span>{workspace?.createdAt ? moment(workspace.createdAt).format('MMM DD, YYYY') : 'N/A'}</span>
                </div>
              </div>

              <div className="user-info workspace-plan-box">
                <div className="user-title-section">
                  <div className="workspace-plan-grid">
                    <div className="workspace-plan-cell">
                      <label>Plan</label>
                      <Badge color={planName === 'Free' ? 'secondary' : 'primary'}>
                        {planInfo.name}{planInfo.type ? ` (${planInfo.type})` : ''}
                      </Badge>
                    </div>
                    {planPrice != null && (
                      <div className="workspace-plan-cell">
                        <label>Price</label>
                        <span className="workspace-plan-cell-value">${formatNumber(planPrice)}{PRICE_INTERVAL_SUFFIX[planInfo.interval] || ''}</span>
                      </div>
                    )}
                    <div className="workspace-plan-cell">
                      <label>Credits Left</label>
                      <span className="workspace-plan-cell-value">{formatNumber(Math.round(parseFloat(subscription?.creditsBalance) || 0))}</span>
                    </div>
                    {workspacePaymentHistory && (
                      <div className="workspace-plan-cell">
                        <label>Total Spent</label>
                        <span className="workspace-plan-cell-value">${formatNumber(workspacePaymentHistory?.totalSpent || 0)}</span>
                      </div>
                    )}
                    <div className="workspace-plan-cell">
                      <label>Billing</label>
                      <span className="workspace-plan-cell-value">{planInfo.interval || 'N/A'}</span>
                    </div>
                    <div className="workspace-plan-cell">
                      <label>Status</label>
                      <span className="workspace-plan-cell-value workspace-plan-cell-cap">{subscription?.status || 'free'}</span>
                    </div>
                    {subscription?.currentPeriodEnd && (
                      <div className="workspace-plan-cell">
                        <label>Renews</label>
                        <span className="workspace-plan-cell-value">{moment(subscription.currentPeriodEnd).format('MMM DD, YYYY')}</span>
                      </div>
                    )}
                    {subscription?.isLocked && (
                      <div className="workspace-plan-cell workspace-plan-cell-locked">
                        <label>Locked</label>
                        <span className="workspace-plan-cell-value">
                          <i className="fa fa-lock mr-1"></i>
                          {LOCK_REASON_LABELS[subscription?.lockReason] || 'Access locked'}
                        </span>
                      </div>
                    )}
                    {!subscription?.isLocked && subscription?.gracePeriodEndsAt && new Date(subscription.gracePeriodEndsAt) > new Date() && (
                      <div className="workspace-plan-cell workspace-plan-cell-grace">
                        <label>Grace Ends</label>
                        <span className="workspace-plan-cell-value">{moment(subscription.gracePeriodEndsAt).format('MMM DD, YYYY')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='function-box workspace-area-detail mt-3'>
              <div className="sidebar-body">
                <div className='tabs-area'>
                  {/* Navigation Tabs */}
                  <div className='tab-header'>
                    {/* Navigation Tabs */}
                    <Nav tabs className="workspace-tabs">
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'members' })}
                          onClick={() => toggle('members')}
                        >
                          Members
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'contacts' })}
                          onClick={() => toggle('contacts')}
                        >
                          Contacts
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'deals' })}
                          onClick={() => toggle('deals')}
                        >
                          Deals
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'agents' })}
                          onClick={() => toggle('agents')}
                        >
                          Agents
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'workspaces' })}
                          onClick={() => toggle('workspaces')}
                        >
                          Workspaces
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'paymentHistory' })}
                          onClick={() => toggle('paymentHistory')}
                        >
                          Payments
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'activity' })}
                          onClick={() => toggle('activity')}
                        >
                          Activity
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </div>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="members">
                    {renderMembersTab()}
                  </TabPane>
                  <TabPane tabId="contacts">
                    {renderContactsTab()}
                  </TabPane>
                  <TabPane tabId="deals">
                    {renderDealsTab()}
                  </TabPane>
                  <TabPane tabId="agents">
                    {renderAgentsTab()}
                  </TabPane>
                  <TabPane tabId="workspaces">
                    {renderWorkspacesTab()}
                  </TabPane>
                  <TabPane tabId="paymentHistory">
                    {renderPaymentHistoryTab()}
                  </TabPane>
                  <TabPane tabId="activity">
                    {renderActivityTab()}
                  </TabPane>
                </TabContent>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* <div className={`workspace-details-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Button
            color="link"
            className="close-btn"
            onClick={onClose}
          >
            <i className="fa fa-times"></i>
          </Button>

          <div className="workspace-info">
            <div className="workspace-title-section">
              <h3 className="workspace-name">
                <i className="fa fa-building-o mr-2 text-primary"></i>
                {workspace?.name || 'Workspace Details'}
              </h3>
              <Badge
                color={workspace?.status === 'active' ? 'success' : workspace?.status === 'suspended' ? 'warning' : 'secondary'}
                className="status-badge"
              >
                {workspace?.status || 'Active'}
              </Badge>
            </div>

            <div className="workspace-meta-grid">
              <div className="meta-card">
                <div className="meta-icon">
                  <i className="fa fa-envelope-o"></i>
                </div>
                <div className="meta-content">
                  <label>Email:</label>
                  <span title={workspace?.owner?.email || workspace?.createdBy?.email || 'N/A'}>
                    {workspace?.owner?.email || workspace?.createdBy?.email || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="meta-card">
                <div className="meta-icon">
                  <i className="fa fa-user"></i>
                </div>
                <div className="meta-content">
                  <label>Owner:</label>
                  <span title={workspace?.owner ?
                    `${workspace.owner.firstName || ''} ${workspace.owner.lastName || ''}`.trim() || 'N/A' :
                    workspace?.createdBy ?
                      `${workspace.createdBy.firstName || ''} ${workspace.createdBy.lastName || ''}`.trim() || 'N/A' :
                      'N/A'}>
                    {workspace?.owner ?
                      `${workspace.owner.firstName || ''} ${workspace.owner.lastName || ''}`.trim() || 'N/A' :
                      workspace?.createdBy ?
                        `${workspace.createdBy.firstName || ''} ${workspace.createdBy.lastName || ''}`.trim() || 'N/A' :
                        'N/A'
                    }
                  </span>
                </div>
              </div>

              <div className="meta-card">
                <div className="meta-icon">
                  <i className="fa fa-calendar"></i>
                </div>
                <div className="meta-content">
                  <label>Created:</label>
                  <span>{workspace?.createdAt ? moment(workspace.createdAt).format('MMM DD, YYYY') : 'N/A'}</span>
                </div>
              </div>

              <div className="meta-card">
                <div className="meta-icon">
                  <i className="fa fa-diamond"></i>
                </div>
                <div className="meta-content">
                  <label>Plan:</label>
                  <Badge color={workspace?.plan === 'Free' || workspace?.subscription?.planName === 'Free' ? 'secondary' : 'primary'}>
                    {workspace?.plan || workspace?.subscription?.planName || 'Free'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sidebar-body">
          <Nav tabs className="workspace-tabs">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'members' })}
                onClick={() => toggle('members')}
              >
                Members
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'contacts' })}
                onClick={() => toggle('contacts')}
              >
                Contacts
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'deals' })}
                onClick={() => toggle('deals')}
              >
                Deals
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'agents' })}
                onClick={() => toggle('agents')}
              >
                Agents
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'workspaces' })}
                onClick={() => toggle('workspaces')}
              >
                Workspaces
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'paymentHistory' })}
                onClick={() => toggle('paymentHistory')}
              >
                Payment History
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'activity' })}
                onClick={() => toggle('activity')}
              >
                Recent Activity
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="members">
              {renderMembersTab()}
            </TabPane>
            <TabPane tabId="contacts">
              {renderContactsTab()}
            </TabPane>
            <TabPane tabId="deals">
              {renderDealsTab()}
            </TabPane>
            <TabPane tabId="agents">
              {renderAgentsTab()}
            </TabPane>
            <TabPane tabId="workspaces">
              {renderWorkspacesTab()}
            </TabPane>
            <TabPane tabId="paymentHistory">
              {renderPaymentHistoryTab()}
            </TabPane>
            <TabPane tabId="activity">
              {renderActivityTab()}
            </TabPane>
          </TabContent>
        </div>
      </div> */}
    </>
  );
};

const PRICE_INTERVAL_SUFFIX = { Monthly: '/mo', Yearly: '/yr', Quarterly: '/qtr' };

// Splits technical plan names like 'Starter_agency_month_<planId>' into display parts — mirrors getPlanBadge in the workspaces table
const parsePlanName = (plan) => {
  if (!plan || plan === '-' || plan === 'Free') return { name: 'Free', type: null, interval: null };
  const parts = plan.split('_');
  const name = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const type = parts[1] ? parts[1].charAt(0).toUpperCase() + parts[1].slice(1) : null;
  const interval = parts[2] === 'month' ? 'Monthly' : parts[2] === 'year' ? 'Yearly' : parts[2] === 'quarter' ? 'Quarterly' : null;
  return { name, type, interval };
};

// Reason: descriptions arrive as server-built strings ("5000 credits added in pool") — comma standalone numbers only, leaving invoice-style codes (ZXGNI6CB-0008) untouched
const formatDescriptionNumbers = (description) => {
  if (!description) return description;
  return description.replace(/(^|[^\w-])(\d{4,})(?![\w-])/g, (match, prefix, num) => `${prefix}${Number(num).toLocaleString('en-US')}`);
};

export default WorkspaceSidebar;